import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import CalendarHighlight from "../types/calendarHighlight";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface CalendarHighlightState {
  highlight: CalendarHighlight;
  highlightSearchResults?: SearchResults;
  highlightFilter: SearchParams;
  highlights: CalendarHighlight[];
  status: "idle" | "loading" | "failed";
}

const initialState: CalendarHighlightState = {
  highlight: {
    id: 0,
    name: "",
    color: "",
  },
  highlightFilter: defaultParams,
  highlights: [],
  status: "idle",
};

export const getHighlight = createAsyncThunk<CalendarHighlight, number>(
  "highlight/get/id",
  async (id: number) => {
    const response = await http.get<CalendarHighlight>(
      `/calendar-highlight/${id}`
    );
    return response.data;
  }
);

export const getHighlights = createAsyncThunk<CalendarHighlight[]>(
  "highlight/get/all",
  async () => {
    const response = await http.get<CalendarHighlight[]>(`/calendar-highlight`);
    return response.data;
  }
);

export const postHighlight = createAsyncThunk<
  CalendarHighlight,
  CalendarHighlight
>("highlight/post", async (highlight: CalendarHighlight) => {
  const response = await http.post<CalendarHighlight>(
    `/calendar-highlight`,
    highlight,
    {
      params: {
        successToast: true,
      },
    }
  );
  return response.data;
});

export const editHighlight = createAsyncThunk<
  CalendarHighlight,
  CalendarHighlight
>("highlight/edit", async (highlight: CalendarHighlight) => {
  const response = await http.put<CalendarHighlight>(
    `/calendar-highlight/${highlight.id}`,
    highlight,
    {
      params: {
        successToast: true,
      },
    }
  );
  return response.data;
});

export const deleteHighlight = createAsyncThunk<CalendarHighlight, number>(
  "highlight/delete",
  async (id: number) => {
    const response = await http.delete<CalendarHighlight>(
      `calendar-highlight/${id}`,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const searchHighlights = createAsyncThunk<SearchResults, SearchParams>(
  "highlight/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(
      `calendar-highlight/search?${qs}`
    );
    return response.data;
  }
);

const calendarHighlightSlice = createSlice({
  name: "calendarHighlight",
  initialState,
  reducers: {
    resetHighlight(state) {
      state.highlight = initialState.highlight;
    },
    setHighlightFilter(state, action) {
      state.highlightFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHighlight.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getHighlight.fulfilled, (state, action) => {
        state.status = "idle";
        state.highlight = action.payload;
      })
      .addCase(getHighlight.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getHighlights.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getHighlights.fulfilled, (state, action) => {
        state.status = "idle";
        state.highlights = action.payload;
      })
      .addCase(getHighlights.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postHighlight.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postHighlight.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postHighlight.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editHighlight.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editHighlight.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editHighlight.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteHighlight.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteHighlight.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteHighlight.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchHighlights.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchHighlights.fulfilled, (state, action) => {
        state.status = "idle";
        state.highlightSearchResults = action.payload;
      })
      .addCase(searchHighlights.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default calendarHighlightSlice.reducer;
export const { resetHighlight, setHighlightFilter } =
  calendarHighlightSlice.actions;
